import { useEffect, useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DataTableAdvanced,
  type DataTableColumn,
  type DataTableFilter,
} from '@/components/ui/data-table-advanced'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  hierarchyService,
  HIERARCHY_EVENT_LABELS,
  type HierarchyEvent,
  type HierarchyEventType,
  type HierarchySnapshot,
} from '@/services/hierarchy'
import { tenantsService, type Tenant } from '@/services/tenants'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { History, RefreshCw, Network, ShieldAlert } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

/**
 * SUB-3.13 · FR59, NFR5. The platform's structural history.
 *
 * ⚠️ THIS IS NOT AN ACCOUNT'S ACTIVITY LOG. That is `activity_logs` and it
 * belongs to the account, visible to its own Admins (FR61, SUB-3.14). This one
 * is the platform's record of who changed the SHAPE of the platform, it is
 * superadmin-only, and the two must not be merged: one discloses the parent and
 * child map that ISO1 hides from a client, and the other must not.
 *
 * Nothing here can be edited or deleted by anybody. `tenant_hierarchy_events`
 * has no write policy and no write grant; the only thing that inserts into it is
 * the trigger on `tenants`.
 */

const ALL = '__all__'

/** Which events read as a change of who holds access, for the badge colour. */
const LINEAGE_EVENTS: HierarchyEventType[] = ['create', 'attach', 'detach', 'transfer']
const DESTRUCTIVE_EVENTS: HierarchyEventType[] = ['delete', 'archive', 'disable']

const eventVariant = (
  type: HierarchyEventType
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (DESTRUCTIVE_EVENTS.includes(type)) return 'destructive'
  if (LINEAGE_EVENTS.includes(type)) return 'default'
  return 'secondary'
}

/**
 * The one-line "what changed", read from the record's own before and after
 * rather than re-derived from the event type. If the two ever disagree, the
 * record is what happened and the label is a guess, so the record wins here.
 */
const describeChange = (event: HierarchyEvent): string => {
  const before: HierarchySnapshot = event.before ?? {}
  const after: HierarchySnapshot = event.after ?? {}

  switch (event.event_type) {
    case 'create':
      return `under ${after.parent_company_name ?? 'a provider'}`
    case 'create_standalone':
      return 'no provider'
    case 'attach':
      return `now under ${after.parent_company_name ?? 'a provider'}`
    case 'detach':
      return `released from ${before.parent_company_name ?? 'its provider'}`
    case 'transfer':
      return `${before.parent_company_name ?? 'unknown'} → ${
        after.parent_company_name ?? 'unknown'
      }`
    case 'enable':
      return after.sub_accounts_allowance == null
        ? 'unlimited'
        : `allowance ${after.sub_accounts_allowance}`
    case 'disable':
      return 'sub-accounts turned off'
    case 'allowance_change':
      return `${before.sub_accounts_allowance ?? 'unlimited'} → ${
        after.sub_accounts_allowance ?? 'unlimited'
      }`
    case 'archive':
    case 'unarchive':
      return before.parent_company_name
        ? `client of ${before.parent_company_name}`
        : 'standalone account'
    case 'delete':
      return before.parent_company_name
        ? `was a client of ${before.parent_company_name}`
        : 'was a standalone account'
    default:
      return ''
  }
}

interface EventRow extends HierarchyEvent {
  event_label: string
  change_summary: string
  actor_label: string
}

export const HierarchyAudit: React.FC = () => {
  const navigate = useNavigate()
  const [events, setEvents] = useState<HierarchyEvent[]>([])
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [tenantFilter, setTenantFilter] = useState<string>(ALL)
  const [typeFilter, setTypeFilter] = useState<string>(ALL)

  const fetchEvents = async (tenantId: string, eventType: string) => {
    try {
      setLoading(true)
      setEvents(
        await hierarchyService.listEvents({
          tenantId: tenantId === ALL ? undefined : tenantId,
          eventTypes:
            eventType === ALL ? undefined : [eventType as HierarchyEventType],
        })
      )
    } catch (err: unknown) {
      console.error('Failed to load the hierarchy audit:', err)
      const detail = (err as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail
      toast.error(detail || 'Could not load the change history')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents(tenantFilter, typeFilter)
  }, [tenantFilter, typeFilter])

  useEffect(() => {
    // Only to label the account filter. A failure here must not stop the
    // history rendering: the history snapshots its own names and does not
    // depend on this list.
    tenantsService
      .list()
      .then(setTenants)
      .catch((err) => console.error('Failed to load accounts for the filter:', err))
  }, [])

  const rows: EventRow[] = useMemo(
    () =>
      events.map((e) => ({
        ...e,
        event_label: HIERARCHY_EVENT_LABELS[e.event_type] ?? e.event_type,
        change_summary: describeChange(e),
        // ⚠️ The email is SNAPSHOTTED on the record. Resolving the id against
        // the live user list instead would show nothing once the account and
        // its users are deleted, which is exactly the case this table exists
        // to explain.
        actor_label:
          e.actor_source === 'system'
            ? 'System (no signed-in user)'
            : e.actor_email ?? e.actor_user_id ?? 'Unknown',
      })),
    [events]
  )

  const columns: DataTableColumn<EventRow>[] = [
    {
      accessorKey: 'occurred_at',
      header: 'When',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {format(new Date(row.original.occurred_at), 'MMM d, yyyy HH:mm')}
        </span>
      ),
    },
    {
      accessorKey: 'company_name',
      header: 'Account',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.company_name}</span>
      ),
    },
    {
      accessorKey: 'event_label',
      header: 'Change',
      cell: ({ row }) => (
        <Badge variant={eventVariant(row.original.event_type)}>
          {row.original.event_label}
        </Badge>
      ),
    },
    {
      accessorKey: 'change_summary',
      header: 'Detail',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.change_summary}
        </span>
      ),
    },
    {
      accessorKey: 'actor_label',
      header: 'Who',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="text-sm">{row.original.actor_label}</span>
          {row.original.actor_source === 'system' && (
            <Badge variant="outline" className="text-xs">
              system
            </Badge>
          )}
        </div>
      ),
    },
  ]

  const filters: DataTableFilter[] = [
    {
      column: 'company_name',
      type: 'text',
      label: 'Account',
      placeholder: 'Filter by account name',
    },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Hierarchy audit</h1>
          <p className="text-muted-foreground mt-1">
            Every structural change to the platform: who made it, when, and what
            it changed from and to
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => fetchEvents(tenantFilter, typeFilter)}
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => navigate('/tenants/hierarchy')}>
            <Network className="mr-2 h-4 w-4" />
            Relationships
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={tenantFilter} onValueChange={setTenantFilter}>
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder="All accounts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All accounts</SelectItem>
            {tenants.map((t) => (
              <SelectItem key={t.tenant_id} value={t.tenant_id}>
                {t.company_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[240px]">
            <SelectValue placeholder="All changes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All changes</SelectItem>
            {(
              Object.keys(HIERARCHY_EVENT_LABELS) as HierarchyEventType[]
            ).map((type) => (
              <SelectItem key={type} value={type}>
                {HIERARCHY_EVENT_LABELS[type]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {tenantFilter !== ALL && (
          <span className="text-sm text-muted-foreground">
            Includes changes where this account was the provider on either side
            of a move
          </span>
        )}
      </div>

      <DataTableAdvanced
        columns={columns}
        data={rows}
        filters={filters}
        loading={loading}
        searchable
        searchPlaceholder="Search accounts and actors..."
        searchKeys={['company_name', 'actor_label', 'change_summary']}
        emptyMessage={
          tenantFilter === ALL && typeFilter === ALL
            ? 'No structural change has been recorded yet. The history starts when this was deployed and is deliberately not backfilled.'
            : 'No change matches this filter.'
        }
        pageSize={25}
      />

      <div className="mt-6 flex items-start gap-2 rounded-lg border bg-card p-4">
        <ShieldAlert className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">
            This record is append-only.
          </span>{' '}
          Nobody can edit or remove an entry, including a super admin, and a
          record survives the deletion of the account it describes. Entries are
          not backfilled, so the history begins at the point this was deployed.
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <History className="h-3 w-3" />
        Showing the {rows.length} most recent matching changes.
      </div>
    </div>
  )
}
